import { useMemo } from "react";
import { lp, ap } from "../../components/mobile/chart-utils";
import {
  BodyText,
  DisplayText,
  Dot,
  Glass,
  ScreenLabel,
  alpha,
  borderAlpha,
} from "../../components/mobile/primitives";

export const SLEEP_DATA = [6.8, 7.2, 7.0, 6.5, 7.8, 7.4, 6.9, 7.5, 7.1, 7.6, 7.3, 7.4];
export const HRV_DATA = [48, 52, 45, 41, 58, 55, 49, 62, 50, 57, 53, 56];

export const ARTICLES = [
  {
    title: "Sleep duration and cardiovascular risk: a review",
    source: "Nature Medicine",
    tag: "Research",
    tagColor: "var(--color-kin-lavender)",
    year: "2024",
  },
  {
    title: "HRV as a biomarker of autonomic recovery during sleep",
    source: "Journal of Sleep Research",
    tag: "Research",
    tagColor: "var(--color-kin-lavender)",
    year: "2023",
  },
  {
    title: "Practical guide to improving deep sleep",
    source: "Sleep Foundation",
    tag: "Guide",
    tagColor: "var(--color-kin-green-soft)",
    year: "2025",
  },
];

export const RECOMMENDATIONS = [
  {
    code: "WD",
    text: "Add a 30-min screen-free wind-down before bed to raise HRV by ~8ms.",
    accent: "var(--color-kin-lavender)",
    impact: "High impact",
  },
  {
    code: "RT",
    text: "Consistent wake time (\u00b115 min) drives +12% sleep efficiency within 2 weeks.",
    accent: "var(--color-kin-green-soft)",
    impact: "High impact",
  },
  {
    code: "AM",
    text: "Move cardio sessions to morning \u2014 evening workouts correlate with \u20134% deep sleep in your data.",
    accent: "var(--color-kin-orange)",
    impact: "Medium",
  },
];

export function InsightScreen() {
  const W = 228,
    H = 82,
    PAD = 8;

  const sleepLine = useMemo(() => lp(SLEEP_DATA, W, H, PAD, 5.6, 8.4), []);
  const sleepArea = useMemo(() => ap(SLEEP_DATA, W, H, PAD, 5.6, 8.4), []);
  const hrvLine = useMemo(() => lp(HRV_DATA, W, H, PAD, 32, 68), []);

  return (
    <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Hero band */}
      <div
        style={{
          margin: "0 -16px",
          padding: "14px 16px 14px",
          background: `linear-gradient(135deg, ${alpha("var(--color-kin-lavender)", "14")}, ${alpha("var(--color-kin-lavender)", "06")})`,
          borderBottom: borderAlpha("var(--color-kin-lavender)", "20"),
        }}
      >
        <BodyText
          size={10}
          color="var(--color-kin-lavender)"
          weight={800}
          style={{ marginBottom: 8 }}
        >
          \u2190 Skills
        </BodyText>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "var(--color-kin-lavender)",
            }}
          />
          <BodyText
            size={8}
            color="var(--color-kin-lavender)"
            weight={700}
            style={{ letterSpacing: "0.1em", textTransform: "uppercase" }}
          >
            Sleep
          </BodyText>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <DisplayText size={22} lineHeight={1.1} color="var(--color-kin-black)">
            Sleep Deep Dive
          </DisplayText>
          <div
            style={{
              background: "rgba(165,144,255,0.15)",
              border: "1px solid rgba(165,144,255,0.35)",
              borderRadius: 22,
              padding: "5px 12px",
              flexShrink: 0,
              marginTop: 2,
              boxShadow: "0 2px 8px -2px rgba(165,144,255,0.25)",
            }}
          >
            <BodyText
              size={9}
              color="var(--color-kin-lavender)"
              weight={800}
              style={{ whiteSpace: "nowrap" }}
            >
              Chat with Dr. Chen \u2192
            </BodyText>
          </div>
        </div>
        <BodyText size={9} color="var(--color-kin-muted)" weight={500} style={{ marginTop: 3 }}>
          Dr. Sara Chen \u00b7 Sleep and recovery
        </BodyText>
        <BodyText size={8} color="#b0a89f" weight={500} style={{ marginTop: 2 }}>
          Run completed 2h ago
        </BodyText>
      </div>

      {/* New data since last run — vault diff prompt */}
      <div
        style={{
          background: "linear-gradient(135deg, rgba(47,181,106,0.08), rgba(47,181,106,0.03))",
          border: "1px solid rgba(47,181,106,0.25)",
          borderRadius: 18,
          padding: "12px 14px",
        }}
      >
        <BodyText
          size={9}
          color="#2fb56a"
          weight={800}
          style={{ letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}
        >
          New data since Mar 2 run
        </BodyText>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingLeft: 2 }}>
          {[
            "8 nights of sleep data (Mar 3\u201310)",
            "1 new lab panel (lipid markers)",
            "Garmin synced 42 new HR readings",
          ].map((item) => (
            <div key={item} style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
              <BodyText size={8} color="#2fb56a" weight={700}>
                \u00b7
              </BodyText>
              <BodyText
                size={9}
                color="var(--color-kin-body)"
                weight={600}
                style={{ lineHeight: 1.4 }}
              >
                {item}
              </BodyText>
            </div>
          ))}
        </div>
        <div
          style={{
            marginTop: 10,
            padding: "6px 14px",
            borderRadius: 22,
            background: "#2fb56a",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            boxShadow: "0 2px 8px -2px rgba(47,181,106,0.4)",
          }}
        >
          <BodyText size={9} color="#ffffff" weight={800}>
            Re-run with new data \u2192
          </BodyText>
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: "flex", gap: 8 }}>
        {[
          { v: "7.4h", l: "Avg Sleep" },
          { v: "84%", l: "Efficiency" },
          { v: "53ms", l: "Avg HRV" },
        ].map((kpi) => (
          <Glass
            key={kpi.l}
            style={{ flex: 1, padding: "10px 6px", textAlign: "center", borderRadius: 22 }}
          >
            <DisplayText size={18} color="var(--color-kin-black)">
              {kpi.v}
            </DisplayText>
            <ScreenLabel style={{ marginTop: 2 }}>{kpi.l}</ScreenLabel>
          </Glass>
        ))}
      </div>

      {/* Dual-metric chart */}
      <div style={{ position: "relative" }}>
        <div
          style={{
            position: "absolute",
            inset: -4,
            background:
              "radial-gradient(circle at 50% 50%, rgba(91,122,102,0.15), transparent 70%)",
            filter: "blur(16px)",
            borderRadius: 22,
            zIndex: 0,
          }}
        />
        <Glass
          style={{ padding: "12px 14px 10px", borderRadius: 22, position: "relative", zIndex: 1 }}
        >
          {/* Header + legend */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <ScreenLabel>Sleep Duration + HRV \u00b7 12 nights</ScreenLabel>
            <div style={{ display: "flex", gap: 10 }}>
              {[
                ["var(--color-kin-green-soft)", "Sleep"],
                ["var(--color-kin-lavender)", "HRV"],
              ].map(([color, label]) => (
                <div key={label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <div
                    style={{
                      width: 14,
                      height: 3,
                      borderRadius: 2,
                      background: color,
                      boxShadow: `0 0 6px ${alpha(color, "80")}`,
                    }}
                  />
                  <BodyText size={8} color="var(--color-kin-muted)" weight={700}>
                    {label}
                  </BodyText>
                </div>
              ))}
            </div>
          </div>

          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: 82 }}>
            <defs>
              <linearGradient id="ig-sg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-kin-green-soft)" stopOpacity="0.25" />
                <stop offset="100%" stopColor="var(--color-kin-green-soft)" stopOpacity="0.01" />
              </linearGradient>
            </defs>
            {/* Grid lines & Labels */}
            {[0, 1, 2, 3].map((i) => {
              const y = PAD + (i * (H - 2 * PAD)) / 3;
              const sleepVal = (8.4 - i * ((8.4 - 5.6) / 3)).toFixed(1);
              const hrvVal = Math.round(68 - i * ((68 - 32) / 3));
              return (
                <g key={i}>
                  <line
                    x1={PAD + 12}
                    x2={W - PAD - 12}
                    y1={y}
                    y2={y}
                    stroke="rgba(0,0,0,0.05)"
                    strokeWidth="1"
                  />
                  <text
                    x={PAD}
                    y={y + 2.5}
                    fill="var(--color-kin-green-soft)"
                    fontSize="7"
                    fontWeight="700"
                    fontFamily="'Manrope', system-ui"
                  >
                    {sleepVal}
                  </text>
                  <text
                    x={W - PAD - 10}
                    y={y + 2.5}
                    fill="var(--color-kin-lavender)"
                    fontSize="7"
                    fontWeight="700"
                    fontFamily="'Manrope', system-ui"
                  >
                    {hrvVal}
                  </text>
                </g>
              );
            })}
            {/* Sleep area fill */}
            <path d={sleepArea} fill="url(#ig-sg)" />
            {/* Sleep line */}
            <path
              d={sleepLine}
              fill="none"
              stroke="var(--color-kin-green-soft)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* HRV line */}
            <path
              d={hrvLine}
              fill="none"
              stroke="var(--color-kin-lavender)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="0"
            />
            {/* HRV terminal dot */}
            {(() => {
              const idx = HRV_DATA.length - 1;
              const x = PAD + (idx / (HRV_DATA.length - 1)) * (W - 2 * PAD);
              const y = PAD + ((68 - HRV_DATA[idx]) / (68 - 32)) * (H - 2 * PAD);
              return (
                <circle
                  cx={x}
                  cy={y}
                  r="4"
                  fill="var(--color-kin-lavender)"
                  stroke="#fff"
                  strokeWidth="2"
                  style={{ filter: "drop-shadow(0 0 4px var(--color-kin-lavender))" }}
                />
              );
            })()}
            {/* Sleep terminal dot */}
            {(() => {
              const idx = SLEEP_DATA.length - 1;
              const x = PAD + (idx / (SLEEP_DATA.length - 1)) * (W - 2 * PAD);
              const y = PAD + ((8.4 - SLEEP_DATA[idx]) / (8.4 - 5.6)) * (H - 2 * PAD);
              return (
                <circle
                  cx={x}
                  cy={y}
                  r="4"
                  fill="var(--color-kin-green-soft)"
                  stroke="#fff"
                  strokeWidth="2"
                  style={{ filter: "drop-shadow(0 0 4px var(--color-kin-green-soft))" }}
                />
              );
            })()}
          </svg>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 6,
              padding: "0 4px",
            }}
          >
            {["N1", "N3", "N6", "N9", "N12"].map((l) => (
              <BodyText key={l} size={8.5} color="var(--color-kin-muted)" weight={700}>
                {l}
              </BodyText>
            ))}
          </div>
        </Glass>
      </div>

      {/* Relevant reading */}
      <div>
        <ScreenLabel style={{ marginBottom: 8, display: "block", paddingLeft: 4 }}>
          Relevant Reading
        </ScreenLabel>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {ARTICLES.map((a) => (
            <Glass key={a.title} style={{ padding: "12px 14px", borderRadius: 22 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <BodyText
                    size={11}
                    color="var(--color-kin-black)"
                    weight={700}
                    style={{ lineHeight: 1.4 }}
                  >
                    {a.title}
                  </BodyText>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
                    <div
                      style={{
                        padding: "2px 8px",
                        borderRadius: 8,
                        background: alpha(a.tagColor, "15"),
                        border: borderAlpha(a.tagColor, "30"),
                        fontSize: 8,
                        fontWeight: 800,
                        color: a.tagColor,
                        fontFamily: "'Manrope', system-ui",
                      }}
                    >
                      {a.tag}
                    </div>
                    <BodyText size={9} color="var(--color-kin-muted)" weight={500}>
                      {a.source} \u00b7 {a.year}
                    </BodyText>
                  </div>
                </div>
                <span
                  style={{
                    color: "var(--color-kin-muted)",
                    fontSize: 13,
                    flexShrink: 0,
                    marginTop: 1,
                    fontWeight: 700,
                  }}
                >
                  \u2197
                </span>
              </div>
            </Glass>
          ))}
        </div>
      </div>

      {/* Recommendations */}
      <div>
        <ScreenLabel style={{ marginBottom: 8, display: "block", paddingLeft: 4 }}>
          Recommendations
        </ScreenLabel>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {RECOMMENDATIONS.map((rec, i) => (
            <Glass
              key={i}
              style={{
                padding: "12px 14px",
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
                borderRadius: 22,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  flexShrink: 0,
                  background: alpha(rec.accent, "20"),
                  border: borderAlpha(rec.accent, "40"),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: `0 0 10px ${alpha(rec.accent, "30")}`,
                }}
              >
                <BodyText
                  size={9}
                  color={rec.accent}
                  weight={800}
                  style={{ letterSpacing: "0.08em" }}
                >
                  {rec.code}
                </BodyText>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 3,
                  }}
                >
                  <BodyText
                    size={8}
                    color={rec.accent}
                    weight={800}
                    style={{ letterSpacing: "0.06em" }}
                  >
                    {i + 1}.
                  </BodyText>
                  <div
                    style={{
                      padding: "1px 6px",
                      borderRadius: 6,
                      background: alpha(rec.accent, "12"),
                      border: borderAlpha(rec.accent, "25"),
                    }}
                  >
                    <BodyText size={7} color={rec.accent} weight={700}>
                      {rec.impact}
                    </BodyText>
                  </div>
                </div>
                <BodyText
                  size={11}
                  color="var(--color-kin-black)"
                  weight={700}
                  style={{ lineHeight: 1.45 }}
                >
                  {rec.text}
                </BodyText>
              </div>
            </Glass>
          ))}
        </div>
      </div>

      {/* Tracker recommendation from report */}
      <div
        style={{
          background: "rgba(255,208,40,0.08)",
          border: "1px solid rgba(255,208,40,0.25)",
          borderRadius: 22,
          padding: "14px 16px",
          boxShadow: "0 4px 12px -4px rgba(255,208,40,0.15)",
        }}
      >
        <ScreenLabel style={{ display: "block", marginBottom: 8 }}>
          <span style={{ color: "#f0b800" }}>To improve the next run</span>
        </ScreenLabel>
        <BodyText
          size={10}
          color="var(--color-kin-body)"
          weight={500}
          style={{ lineHeight: 1.5, marginBottom: 10 }}
        >
          Your afternoon energy dips correlate with meal timing gaps. Log meals and caffeine for 7
          days so I can test whether that's the driver.
        </BodyText>
        <div
          style={{
            background: "rgba(255,255,255,0.75)",
            border: "1px solid rgba(255,208,40,0.30)",
            borderRadius: 16,
            padding: "12px 14px",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 9,
              background: "rgba(255,208,40,0.14)",
              border: "1px solid rgba(255,208,40,0.30)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Dot color="var(--color-kin-gold)" size={8} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <BodyText size={11} weight={800} color="#1b1d1a" style={{ marginBottom: 1 }}>
              Diet + Caffeine Tracker
            </BodyText>
            <BodyText size={9} color="#7e8b82" weight={500}>
              3 quick logs/day \u00b7 ~30 sec each
            </BodyText>
          </div>
          <div
            style={{
              background: "var(--color-kin-gold)",
              borderRadius: 22,
              padding: "6px 14px",
              flexShrink: 0,
              boxShadow: "0 2px 8px -2px rgba(255,208,40,0.4)",
            }}
          >
            <BodyText size={9} color="#1b1d1a" weight={800}>
              Approve \u2192
            </BodyText>
          </div>
        </div>
        {/* Proposal explanation */}
        <BodyText
          size={7.5}
          color="#7e8b82"
          weight={500}
          style={{ marginTop: 8, paddingLeft: 38, lineHeight: 1.4 }}
        >
          Adds a tracker to your Today screen. You can remove it anytime.
        </BodyText>
      </div>
    </div>
  );
}
